/**
 * The change card's actions row with Undo (phase 3).
 *
 * The grid of the changed spreadsheet is suspended (Univer unmounted) BEFORE
 * the request and resumed after it, as a fresh mount whatever the outcome
 * (P3-D1): an open Univer grid does not pick up a server-side revert.
 * The outcome lives in this component's state only (P3-D4): after a reload a
 * replayed card offers Undo again, and a second press answers EDITED_SINCE.
 * An undo the agent ran from chat sets the same state from `agentOutcome`.
 *
 * The token goes in the POST body and nowhere else: not in a URL, analytics,
 * a log or an error report.
 */

import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { ROUTES } from '../../../constants/routes';
import {
  SpreadsheetUndoError,
  UNDO_FALLBACK_MESSAGE,
  useUndoSpreadsheetChange,
} from '../../../hooks/data/useUndoSpreadsheetChange';
import {
  useSpreadsheetGridStore,
  type OpenHistoryNavState,
} from '../../../stores/spreadsheetGridStore';
import { safeAnalytics } from '../../../utils/analytics';
import { MaterialIcon } from '../../ui/MaterialIcon';
import type { InternalLinkPolicy } from './EntityLink';
import {
  agentUndoFailurePlan,
  UNDO_COPY,
  undoFailurePlan,
  type UndoFailurePlan,
} from './spreadsheetChangeCardCopy';

/**
 * Undo had focus when pressed, and the button is disabled while pending, so
 * the browser drops focus to the body. Once the outcome is in, put it back:
 * on the status text when Undo goes away (Undone, or a final refusal), on
 * Undo itself when it stays for a retry.
 */
function useStatusFocus(state: UndoState, showUndo: boolean) {
  const hadFocus = useRef(false);
  const undoRef = useRef<HTMLButtonElement>(null);
  const statusRef = useRef<HTMLElement | null>(null);
  const setStatus = useCallback((el: HTMLElement | null) => {
    statusRef.current = el;
  }, []);
  useEffect(() => {
    if (state.kind === 'idle' || state.kind === 'pending') return;
    const restore = hadFocus.current;
    hadFocus.current = false;
    if (!restore) return;
    const target = showUndo ? undoRef.current : statusRef.current;
    target?.focus({ preventScroll: true });
  }, [state, showUndo]);
  const notePress = () => {
    hadFocus.current = document.activeElement === undoRef.current;
  };
  return { undoRef, setStatus, notePress };
}

type UndoState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'undone' }
  | ({ readonly kind: 'failed'; readonly message: string } & UndoFailurePlan);

/**
 * Undone, or an outcome that may have happened (UNCONFIRMED, or no clean
 * answer: a network error or a 5xx other than 503). Two paths can answer for one
 * token (this button and the agent's undo from chat), and the second to land
 * usually says EDITED_SINCE because the first reverted it. So a plain refusal
 * never replaces either of these.
 */
function outranksRefusal(state: UndoState): boolean {
  return state.kind === 'undone' || (state.kind === 'failed' && state.mayHaveHappened);
}

/** The state an agent undo from chat leaves the card in (see `agentUndoFailurePlan`). */
function agentOutcomeState(outcome: string): UndoState {
  if (outcome === 'undone') return { kind: 'undone' };
  const plan = agentUndoFailurePlan(outcome);
  return {
    kind: 'failed',
    ...plan,
    message: plan.message ?? UNDO_FALLBACK_MESSAGE,
  };
}

export function SpreadsheetUndoAction({
  artifactId,
  token,
  changeCount,
  landedCount,
  onSpreadsheetPage,
  internalLinkPolicy,
  agentOutcome,
  children,
}: {
  readonly artifactId: string;
  readonly token: string;
  readonly changeCount: number;
  readonly landedCount: number;
  /** The grid for this artifact is on screen, so history opens in place. */
  readonly onSpreadsheetPage: boolean;
  readonly internalLinkPolicy: InternalLinkPolicy;
  /** The latest outcome of the agent's undo of this change from chat, if any. */
  readonly agentOutcome?: string;
  /** The rest of the row ("Open spreadsheet"), after Undo. */
  readonly children?: ReactNode;
}) {
  const [state, setState] = useState<UndoState>({ kind: 'idle' });
  // The disabled button covers a click after re-render; this covers two
  // clicks that land before it.
  const inFlight = useRef(false);
  const { mutateAsync } = useUndoSpreadsheetChange();
  // Actions only, read at call time: no card re-renders on a grid change.
  const { suspend, resume, requestHistory } = useSpreadsheetGridStore.getState();
  const navigate = useNavigate();
  const counts = { change_count: changeCount, landed_count: landedCount };

  const showUndo =
    state.kind === 'idle' ||
    state.kind === 'pending' ||
    (state.kind === 'failed' && state.canRetry);
  const focus = useStatusFocus(state, showUndo);

  // The agent undid (or tried to undo) this change from chat: show what this
  // button would have. The grid remount is the chat dispatch's, not ours.
  // While a press is in flight only undone or unconfirmed applies: a plain
  // refusal would clear the pending state under a request whose own answer
  // is still coming. Undone is final, and unconfirmed yields only to undone.
  useEffect(() => {
    if (!agentOutcome) return;
    const next = agentOutcomeState(agentOutcome);
    if (inFlight.current && !outranksRefusal(next)) return;
    setState((current) =>
      current.kind === 'undone' || (outranksRefusal(current) && !outranksRefusal(next))
        ? current
        : next,
    );
  }, [agentOutcome]);

  // `mutateAsync`, not `mutate` with callbacks: those never run once the card
  // unmounts, and the grid would stay suspended.
  const undo = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    focus.notePress();
    safeAnalytics.track(ANALYTICS_EVENTS.SPREADSHEET_CHANGE_CARD_UNDO_CLICKED, counts);
    setState({ kind: 'pending' });
    suspend(artifactId);
    try {
      await mutateAsync({ artifactId, token });
      resume(artifactId);
      setState({ kind: 'undone' });
      safeAnalytics.track(ANALYTICS_EVENTS.SPREADSHEET_CHANGE_CARD_UNDONE, counts);
    } catch (error) {
      const code = error instanceof SpreadsheetUndoError ? error.code : 'UNKNOWN_ERROR';
      const status = error instanceof SpreadsheetUndoError ? error.status : undefined;
      const hasServerMessage = !(error instanceof SpreadsheetUndoError) || error.hasServerMessage;
      const plan = undoFailurePlan(code, status, hasServerMessage);
      resume(artifactId);
      const message = plan.message ?? (error instanceof Error ? error.message : '');
      const failed: UndoState = {
        kind: 'failed',
        ...plan,
        message: message || UNDO_COPY.unknownOutcome,
      };
      // The agent's undo may have landed while this request was out, so this
      // answer (usually EDITED_SINCE) must not downgrade Undone or unconfirmed.
      setState((current) => (outranksRefusal(current) ? current : failed));
    } finally {
      inFlight.current = false;
    }
  };

  // History opens in place on the page. Elsewhere, and only where the card may
  // navigate at all, the request rides the navigation itself: the page opens
  // history once it arrives and its grid is ready. A navigation an
  // unsaved-changes prompt blocks never arrives, so nothing is left behind.
  const canOpenHistory = onSpreadsheetPage || internalLinkPolicy === 'navigate';
  const openHistory = () => {
    if (onSpreadsheetPage) {
      requestHistory(artifactId);
      return;
    }
    const state: OpenHistoryNavState = { openHistory: true };
    void navigate(ROUTES.spreadsheet(artifactId), { state });
  };

  return (
    <VStack align="stretch" spacing={1}>
      {/* Always mounted, so the outcome is announced; it holds only the
          outcome text, never the buttons or the pending label. */}
      <Box role="status">
        {state.kind === 'failed' && (
          <Text
            ref={focus.setStatus}
            tabIndex={-1}
            fontSize="xs"
            color="text.secondary"
            textAlign="right"
          >
            {state.message}
          </Text>
        )}
        {state.kind === 'undone' && (
          <HStack
            ref={focus.setStatus}
            tabIndex={-1}
            spacing={1}
            w="fit-content"
            ml="auto"
            color="text.muted"
          >
            <MaterialIcon name="check" boxSize={3.5} color="inherit" aria-hidden />
            <Text fontSize="xs">{UNDO_COPY.undone}</Text>
          </HStack>
        )}
      </Box>
      <HStack spacing={2} justify="flex-end">
        {state.kind === 'failed' && state.offerHistory && canOpenHistory && (
          <Button
            size="xs"
            variant="secondary"
            leftIcon={<MaterialIcon name="history" boxSize={3.5} color="inherit" />}
            onClick={openHistory}
          >
            {UNDO_COPY.history}
          </Button>
        )}
        {showUndo && (
          <Button
            size="xs"
            variant="secondary"
            ref={focus.undoRef}
            leftIcon={<MaterialIcon name="undo" boxSize={3.5} color="inherit" />}
            isLoading={state.kind === 'pending'}
            loadingText={UNDO_COPY.undoing}
            isDisabled={state.kind === 'pending'}
            onClick={() => void undo()}
          >
            {UNDO_COPY.undo}
          </Button>
        )}
        {children}
      </HStack>
    </VStack>
  );
}
